import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Badge from '../components/Badge.jsx';
import OutreachStageDot, { stageTextColor } from '../components/OutreachStageDot.jsx';
import ColumnToggle, { LayoutDropdown, useColumnPreferences } from '../components/ColumnToggle.jsx';
import ColumnFilterMenu from '../components/ColumnFilterMenu.jsx';
import useColumnFilters from '../components/useColumnFilters.js';

// Distributors list view (Dave 2026-06-13). Rebuilt to match the
// Sales Leads table structure now that Zoe runs distributor outreach
// for demo arrangements: same column-toggle / layouts / outreach
// stage / owner / last-email-status pattern as Leads, minus the
// site-specific columns (site_number / pos_type / fuel_brand /
// install_date) that don't apply to distributors. Territory chip
// strip stays since it's the primary distributor segmentation.

function formatDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

function contactSubtext(d) {
  const parts = [d.first_name, d.last_name].filter(Boolean).join(' ');
  return parts || d.position || '';
}

function daysBetween(targetISO, todayISO) {
  if (!targetISO) return null;
  const a = new Date(targetISO + 'T00:00:00');
  const b = new Date(todayISO + 'T00:00:00');
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return null;
  return Math.round((b - a) / (1000 * 60 * 60 * 24));
}

function NextContactCell({ value, todayISO }) {
  const formatted = formatDate(value);
  if (!formatted) return '';
  const days = daysBetween(formatted, todayISO);
  let dotClass = 'status-dot-grey';
  if (days != null) {
    if (days > 0) dotClass = 'status-dot-red';
    else if (days === 0) dotClass = 'status-dot-amber';
    else dotClass = 'status-dot-green';
  }
  return (
    <>
      <span className={`status-dot ${dotClass}`} />
      <span className="cell-mono">{formatted}</span>
    </>
  );
}

function truncateCell(s, max = 24) {
  if (!s) return '';
  const v = String(s);
  return v.length > max ? v.slice(0, max - 1) + '…' : v;
}

// Column registry -- mirrors Leads.jsx COLUMNS pattern. Groups:
//   contact     -- name, status, last email
//   address     -- address1/2, city, province, postal, country, owner
//   phoneEmail  -- phones + emails
//   distributor -- the distributor-specific fields (territory, sales
//                  person, sites serviced, referrals, rate %, last/next
//                  contact, sub-contacts count)
const COLUMNS = [
  // ── Contact Information ─────────────────────────────────────────
  {
    key: 'company', label: 'Company', group: 'contact',
    filterValue: (d) => d.company || '',
    cell: (d) => {
      const subtext = contactSubtext(d);
      const stageColor = stageTextColor(d.outreach_stage);
      const style = stageColor
        ? { display: 'flex', alignItems: 'center', color: stageColor, fontWeight: 'var(--font-semibold)' }
        : { display: 'flex', alignItems: 'center' };
      return (
        <>
          <div className="cell-primary" style={style}>
            <OutreachStageDot stage={d.outreach_stage} />
            {d.company || '—'}
          </div>
          {subtext && <div className="cell-subtext">{subtext}</div>}
        </>
      );
    }
  },
  { key: 'first_name', label: 'First Name', group: 'contact', defaultHidden: true,
    filterValue: (d) => d.first_name || '',
    cell: (d) => d.first_name || '' },
  { key: 'last_name', label: 'Last Name', group: 'contact', defaultHidden: true,
    filterValue: (d) => d.last_name || '',
    cell: (d) => d.last_name || '' },
  { key: 'position', label: 'Position', group: 'contact', defaultHidden: true,
    filterValue: (d) => d.position || '',
    cell: (d) => d.position || '' },
  { key: 'current_status', label: 'Status', group: 'contact',
    filterValue: (d) => d.current_status || '',
    cell: (d) => d.current_status
      ? <Badge tone="teal">{d.current_status}</Badge>
      : '—' },
  { key: 'last_email_status', label: 'Last Email', group: 'contact',
    filterValue: (d) => d.last_email_status || '(no email sent)',
    cell: (d) => {
      if (!d.last_email_status) return '—';
      const tone = d.last_email_status === 'Opened'    ? 'green'
                : d.last_email_status === 'Delivered'  ? 'teal'
                : d.last_email_status === 'Sent'       ? 'grey'
                : d.last_email_status === 'Bounced'    ? 'red'
                :                                        'grey';
      return <Badge tone={tone}>{d.last_email_status}</Badge>;
    },
  },
  { key: 'website', label: 'Website', group: 'contact', defaultHidden: true,
    tdClass: 'cell-mono',
    filterValue: (d) => d.website || '',
    cell: (d) => truncateCell(d.website, 32) },

  // ── Address ─────────────────────────────────────────────────────
  { key: 'address1', label: 'Address 1', group: 'address', defaultHidden: true,
    filterValue: (d) => d.address1 || '',
    cell: (d) => truncateCell(d.address1, 32) },
  { key: 'address2', label: 'Address 2', group: 'address', defaultHidden: true,
    filterValue: (d) => d.address2 || '',
    cell: (d) => truncateCell(d.address2, 32) },
  { key: 'city', label: 'City', group: 'address',
    filterValue: (d) => d.city || '',
    cell: (d) => d.city || '' },
  { key: 'province', label: 'Province / State', group: 'address',
    filterValue: (d) => d.province_state || d.state || '',
    cell: (d) => d.province_state || d.state || '' },
  // Owner sits in the address cluster (between Province and the next
  // contact/phone block) so "who's working this" reads at a glance
  // -- matches the Customers/Leads convention.
  { key: 'owner', label: 'Owner', group: 'distributor',
    filterValue: (d) => d.owner_name || '(unassigned)',
    cell: (d) => d.owner_name
      ? <Badge tone="teal">{d.owner_name}</Badge>
      : <span className="cell-muted">unassigned</span> },
  { key: 'postal_zip', label: 'Postal / Zip', group: 'address', defaultHidden: true,
    tdClass: 'cell-mono',
    filterValue: (d) => d.postal_zip || '',
    cell: (d) => d.postal_zip || '' },
  { key: 'country', label: 'Country', group: 'address', defaultHidden: true,
    filterValue: (d) => d.country || '',
    cell: (d) => d.country || '' },

  // ── Phone & Email ───────────────────────────────────────────────
  { key: 'phone_main', label: 'Phone Main', group: 'phoneEmail', tdClass: 'cell-mono',
    filterValue: (d) => d.phone_main || '',
    cell: (d) => d.phone_main || '' },
  { key: 'phone_cell', label: 'Phone Cell', group: 'phoneEmail', defaultHidden: true,
    tdClass: 'cell-mono',
    filterValue: (d) => d.phone_cell || '',
    cell: (d) => d.phone_cell || '' },
  { key: 'phone_fax', label: 'Phone Fax', group: 'phoneEmail', defaultHidden: true,
    tdClass: 'cell-mono',
    filterValue: (d) => d.phone_fax || '',
    cell: (d) => d.phone_fax || '' },
  { key: 'email', label: 'Email', group: 'phoneEmail',
    filterValue: (d) => d.email1 || '',
    cell: (d) => <div className="cell-primary cell-mono">{d.email1 || ''}</div> },
  { key: 'email2', label: 'Email 2', group: 'phoneEmail', defaultHidden: true,
    tdClass: 'cell-mono',
    filterValue: (d) => d.email2 || '',
    cell: (d) => d.email2 || '' },
  { key: 'email3', label: 'Email 3', group: 'phoneEmail', defaultHidden: true,
    tdClass: 'cell-mono',
    filterValue: (d) => d.email3 || '',
    cell: (d) => d.email3 || '' },

  // ── Distributor-specific ────────────────────────────────────────
  { key: 'territory', label: 'Territory', group: 'distributor',
    filterValue: (d) => d.territory || '',
    cell: (d) => d.territory || '' },
  { key: 'sales_person', label: 'Their Sales Person', group: 'distributor',
    filterValue: (d) => d.sales_person || '',
    cell: (d) => d.sales_person || '' },
  { key: 'passport_sites_serviced', label: 'Sites Serviced', group: 'distributor',
    tdClass: 'cell-mono',
    filterValue: (d) => String(d.passport_sites_serviced ?? ''),
    cell: (d) => d.passport_sites_serviced ?? '' },
  { key: 'referrals_sent', label: 'Referrals', group: 'distributor',
    tdClass: 'cell-mono',
    filterValue: (d) => String(d.referrals_sent ?? 0),
    cell: (d) => d.referrals_sent ?? 0 },
  { key: 'referral_rate', label: 'Rate %', group: 'distributor',
    tdClass: 'cell-mono',
    filterValue: (d) => d.referral_rate != null ? `${d.referral_rate}%` : '',
    cell: (d) => d.referral_rate != null ? `${d.referral_rate}%` : '' },
  { key: 'last_contact', label: 'Last Contact', group: 'distributor',
    tdClass: 'cell-mono', defaultHidden: true,
    filterValue: (d) => formatDate(d.last_contact) || '(never)',
    cell: (d) => formatDate(d.last_contact) },
  { key: 'next_contact', label: 'Next Contact', group: 'distributor',
    filterValue: (d) => formatDate(d.next_contact) || '(none)',
    cell: (d, ctx) => <NextContactCell value={d.next_contact} todayISO={ctx.todayISO} /> },
  { key: 'contacts_count', label: 'Sub-contacts', group: 'distributor', defaultHidden: true,
    tdClass: 'cell-mono',
    filterValue: (d) => String(d.contacts_count ?? 0),
    cell: (d) => d.contacts_count ?? 0 },
];

const DEFAULT_KEYS = COLUMNS.filter((c) => !c.defaultHidden).map((c) => c.key);
const ALL_KEYS     = COLUMNS.map((c) => c.key);

export default function Distributors() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  // null = "All territories"
  const [territory, setTerritory] = useState(null);
  const navigate = useNavigate();
  const todayISO = useMemo(() => new Date().toISOString().slice(0, 10), []);

  // Column-toggle + saved-layouts hookup. Same shape as Customers/Leads.
  const {
    visibleKeys, setVisibleKeys,
    layouts, activeName, setActiveName,
    saveLayout, deleteLayout
  } = useColumnPreferences('distributors', DEFAULT_KEYS, ALL_KEYS);

  // Per-column filter helpers from useColumnFilters: applyFilters
  // filters an array against the currently-set per-column filters.
  // We also pull `filters`, `setFilter`, `clearFilter`, `valuesByKey`
  // out so each column header can render its own ColumnFilterMenu.
  const {
    filters, setFilter, clearFilter, valuesByKey, applyFilters
  } = useColumnFilters(rows, COLUMNS);

  // Click-to-sort state. null sortBy = use server's default order.
  // Clicking a header sets it as the sort column (asc); clicking again
  // flips direction. Dave 2026-06-22 — wants every column sortable.
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState('asc'); // 'asc' | 'desc'

  function toggleSort(key) {
    if (sortBy === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir('asc');
    }
  }

  function sortArrow(key) {
    if (sortBy !== key) return '';
    return sortDir === 'asc' ? ' ▲' : ' ▼';
  }

  useEffect(() => {
    let cancelled = false;
    axios.get('/api/distributors')
      .then((r) => { if (!cancelled) setRows(r.data?.distributors || []); })
      .catch((e) => { if (!cancelled) setError(e.response?.data?.error || e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const territories = useMemo(() => {
    const s = new Set();
    for (const r of rows) if (r.territory) s.add(r.territory);
    return Array.from(s).sort();
  }, [rows]);

  // Final visible list -- territory chip + free-text search applied
  // on top of the per-column filters from useColumnFilters, then
  // sorted by the currently-selected column (if any).
  const visible = useMemo(() => {
    let out = rows;
    if (territory) out = out.filter((r) => r.territory === territory);
    const q = query.trim().toLowerCase();
    if (q) {
      out = out.filter((r) => {
        const hay = [
          r.company, r.first_name, r.last_name,
          r.city, r.province_state, r.state, r.territory,
          r.system_type, r.sales_person, r.phone_main, r.email1
        ].map((v) => (v == null ? '' : String(v).toLowerCase())).join(' | ');
        return hay.includes(q);
      });
    }
    out = applyFilters(out);

    // Click-to-sort. Use the column's filterValue accessor where
    // available (matches what the user sees in the cell); fall back
    // to the raw field value keyed by column.key. localeCompare with
    // numeric: true gets us natural "1, 2, 10" sorting for free.
    if (sortBy) {
      const col = COLUMNS.find((c) => c.key === sortBy);
      const getVal = (r) => {
        if (col && typeof col.filterValue === 'function') return col.filterValue(r) ?? '';
        return r?.[sortBy] ?? '';
      };
      out = [...out].sort((a, b) => {
        const av = String(getVal(a) ?? '');
        const bv = String(getVal(b) ?? '');
        const cmp = av.localeCompare(bv, undefined, { numeric: true, sensitivity: 'base' });
        return sortDir === 'asc' ? cmp : -cmp;
      });
    }
    return out;
  }, [rows, territory, query, applyFilters, sortBy, sortDir]);

  return (
    <section className="page">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h2>Distributors</h2>
          <div className="subtitle">Referral partners & demo prospects</div>
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
          <Link to="/distributors/new" className="btn-primary">+ New Distributor</Link>
        </div>
      </div>

      {/* ─── Territory filter strip ──────────────────────────────── */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-3)',
        marginBottom: 'var(--space-3)',
        flexWrap: 'wrap'
      }}>
        <div style={{ display: 'flex', gap: 'var(--space-1)', flexWrap: 'wrap', alignItems: 'center' }}>
          <button
            type="button"
            className={territory === null ? 'btn-primary btn-sm' : 'btn-secondary btn-sm'}
            onClick={() => setTerritory(null)}
          >
            All territories
          </button>
          {territories.map((t) => (
            <button
              key={t}
              type="button"
              className={territory === t ? 'btn-primary btn-sm' : 'btn-secondary btn-sm'}
              onClick={() => setTerritory(t === territory ? null : t)}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* ─── Toolbar: search + result count + layouts + columns ──── */}
      <div className="toolbar">
        <input
          type="search"
          className="search-input"
          placeholder="Search company, contact, city, sales person, email…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoFocus
        />
        <span className="result-count">
          {loading ? 'Loading…' : `${visible.length} of ${rows.length}`}
        </span>
        <div className="toolbar-prefs" style={{ marginLeft: 'auto' }}>
          <LayoutDropdown
            layouts={layouts}
            activeName={activeName}
            onChange={setActiveName}
          />
          <ColumnToggle
            columns={COLUMNS.map(({ key, label, group }) => ({ key, label, group }))}
            value={visibleKeys}
            onChange={setVisibleKeys}
            activeName={activeName}
            onSaveAs={(name) => saveLayout(name, visibleKeys)}
            onDelete={deleteLayout}
          />
        </div>
      </div>

      {error && <p className="error">{error}</p>}
      {!loading && !error && rows.length === 0 && (
        <p className="panel-empty">No distributors yet. Add one with "+ New Distributor".</p>
      )}

      {!loading && !error && rows.length > 0 && (
        <div className="table-wrap">
          <table className="data-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                {COLUMNS.filter((c) => visibleKeys.includes(c.key)).map((c) => {
                  const filterable = typeof c.filterValue === 'function';
                  const isFilterActive = filterable && !!filters[c.key];
                  return (
                    <th
                      key={c.key}
                      className={isFilterActive ? 'th-filter-active' : ''}
                      style={{ textAlign: c.tdClass === 'cell-mono' ? 'right' : 'left' }}
                    >
                      <span
                        className="th-label"
                        onClick={() => toggleSort(c.key)}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        title="Click to sort"
                      >
                        {c.label}{sortArrow(c.key)}
                      </span>
                      {filterable && (
                        <ColumnFilterMenu
                          label={c.label}
                          values={valuesByKey[c.key] || []}
                          selected={filters[c.key]}
                          onChange={(set) => setFilter(c.key, set)}
                          onClear={() => clearFilter(c.key)}
                        />
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 && (
                <tr>
                  <td colSpan={visibleKeys.length} className="empty-row">
                    No distributors match the current filters.
                  </td>
                </tr>
              )}
              {visible.map((d) => (
                <tr
                  key={d.id}
                  onClick={() => navigate(`/distributors/${d.id}`)}
                  style={{ cursor: 'pointer' }}
                >
                  {COLUMNS.filter((c) => visibleKeys.includes(c.key)).map((c) => (
                    <td key={c.key} className={c.tdClass || undefined}>
                      {c.cell(d, { todayISO })}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
